<template>
    <div class="bwop">
        <div
            ref="bgVideoParallax"
            class="background-video-parallax vm"
            :class="{ enter: isMouseEnter }"
            @mouseenter="listenMouseEnter"
            @mouseleave="listenMouseLeave"
        >
            <video
                class="parallax-video"
                autoplay
                loop
                muted
                playsinline
                :poster="data?.imgPreload?.url"
                v-bind:key="data?.imgPreload?.url"
                :style="{ transform: 'translate(' + mouseX + 'px, ' + mouseY + 'px)' }"
                @loadedmetadata="speedUpVideo"
            >
                <source
                    v-bind:key="data?.img?.url"
                    :src="data?.img?.url"
                    type="video/mp4"
                >
            </video>
            <div
                class="hero-glow"
                aria-hidden="true"
            ></div>
            <div class="content">
                <Teaser
                    class="left mobile-stores-teaser"
                    :title="accentedTitle"
                    :description="data?.description"
                    :buttons="data?.buttons"
                />
            </div>
            <a
                class="hero-scroll"
                href="#how-it-works"
                @click.prevent="scrollDown"
            >
                <span>{{ copy.scroll }}</span>
                <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden="true"
                >
                    <path
                        d="M6 9l6 6 6-6"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </a>
        </div>
    </div>
</template>

<script>
import { mapState } from 'pinia'
import { useStore } from '@/stores/index'
import { getHomeContent } from '@/data/home'
import Teaser from '@/components/Text/Teaser.vue'

export default {
    components: {
        Teaser
    },
    props: {
        data: Object
    },
    computed: {
        ...mapState(useStore, ['language']),
        copy() {
            return getHomeContent(this.language).hero
        },
        /* Everything after the last <br> is wrapped in a pink span
           (the "just in case." moment). Single-line titles pass through. */
        accentedTitle() {
            const title = this.data?.title
            if (!title) return title
            const idx = title.lastIndexOf('<br')
            if (idx === -1) return title
            const close = title.indexOf('>', idx)
            if (close === -1) return title
            const tail = title.slice(close + 1)
            if (!tail.trim()) return title
            return `${title.slice(0, close + 1)}<span class="hero-accent">${tail}</span>`
        },
    },
    data() {
        return {
            isMouseEnter: false,
            mouseX: 0,
            mouseY: 0,
            force: 10,
            el: null
        }
    },
    mounted() {
        this.el = this.$refs['bgVideoParallax']
        if (this.el) {
            this.el.addEventListener('mousemove', this.listenMouseMove, true)
        }
    },
    unmounted() {
        if (this.el) this.el.removeEventListener('mousemove', this.listenMouseMove)
    },
    methods: {
        /* On loadedmetadata (not mounted): the keyed video element is
           recreated when Strapi data lands, which would drop a rate
           set earlier. */
        speedUpVideo(e) {
            e.target.playbackRate = 1.5
        },
        scrollDown() {
            const target = document.getElementById('how-it-works')
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' })
            } else {
                window.scrollBy({ top: window.innerHeight * 0.9, behavior: 'smooth' })
            }
        },
        listenMouseMove(e) {
            this.mouseX = (e.clientX / window.innerWidth) * this.force
            this.mouseY = (e.clientY / window.innerHeight) * this.force
        },
        listenMouseEnter() {
            this.isMouseEnter = true
            window.setTimeout(() => {
                this.listenMouseLeave()
            }, 50)
        },
        listenMouseLeave() {
            this.isMouseEnter = false
        }
    },
}
</script>

<style lang="scss" scoped>
.bwop {
    position: relative;
    padding: 0 !important;
}

.background-video-parallax {
    position: relative;
    width: 100%;
    min-height: 560px;
    height: calc(100vh - #{$header-height});
    overflow: hidden;
    padding: 0 !important;
    z-index: 0;
    background-color: var(--bzioup-color-night);
    flex-direction: column;
}

.parallax-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 1;
    filter: grayscale(100%) brightness(0.3);
    scale: 1.2;
    animation: hero-video-drift 22s ease-in-out infinite;
}

@keyframes hero-video-drift {
    0%, 100% { scale: 1.2; }
    50%      { scale: 1.32; }
}

/* Soft pink nebula behind the text plus a fade into the next band so
   the hero doesn't end on a hard edge. */
.hero-glow {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background:
        radial-gradient(ellipse 60% 50% at 50% 55%, rgba(218, 138, 139, 0.22), transparent 70%),
        linear-gradient(to bottom, rgba(11, 11, 14, 0.35), transparent 30%, transparent 75%, var(--bzioup-color-night) 100%);
}

.content {
    position: relative;
    z-index: 3;
    width: 100%;
    max-width: 760px;
    box-sizing: border-box;
    padding: 40px 24px;
    color: var(--bzioup-color-light);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.content :deep(.text-teaser) {
    max-width: 100%;
    text-align: center;
    animation: hero-rise 900ms cubic-bezier(0.2, 0.8, 0.3, 1) both;
}

.content :deep(.text-teaser h1) {
    font-size: clamp(40px, 7vw, 76px);
    line-height: 1.05;
    margin: 0;
}

.content :deep(.text-teaser h3) {
    font-weight: 500;
    font-size: clamp(16px, 2vw, 20px);
    line-height: 1.5;
    max-width: 520px;
    margin: 18px auto 0;
    opacity: 0.85;
}

.content :deep(.text-teaser > div) {
    justify-content: center;
    margin-top: 28px;
}

.content :deep(.text-teaser a img) {
    height: 52px;
    width: auto;
}

.content :deep(.hero-accent) {
    color: var(--bzioup-color-pink);
}

.hero-scroll {
    position: absolute;
    left: 50%;
    bottom: 28px;
    transform: translateX(-50%);
    z-index: 3;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    letter-spacing: 1px;
    text-transform: uppercase;
    text-decoration: none;
    transition: color 180ms ease;

    svg {
        animation: hero-scroll-bob 1.8s ease-in-out infinite;
    }

    &:hover {
        color: #fff;
    }
}

@keyframes hero-rise {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
}

@keyframes hero-scroll-bob {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(5px); }
}

@media (max-width: $mq-small) {
    .content {
        padding: 32px 20px 72px;
    }

    .content :deep(.text-teaser a img) {
        height: 42px;
    }

    .hero-scroll {
        bottom: 18px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .parallax-video,
    .hero-scroll svg {
        animation: none;
    }
}
</style>
